import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useLocation } from 'react-router-dom';

interface LoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  loadingText: string;
  setLoadingText: (text: string) => void;
  loadingSubtitle: string;
  setLoadingSubtitle: (subtitle: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");
  const [loadingSubtitle, setLoadingSubtitle] = useState("Please wait while we process your request...");
  const location = useLocation();

  // Auth sayfalarında loading'i devre dışı bırak
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  // Sayfa refresh handler
  const handlePageRefresh = () => {
    if (isAuthPage) return; // Auth sayfalarında refresh loading'i gösterme
    // Refresh sırasında global loading göstermeyelim
  };

  // Sayfa refresh hook'unu kullan
  usePageRefresh(handlePageRefresh);

  // Sayfa yüklendiğinde loading'i göster
  useEffect(() => {
    const handlePageRefresh = () => {
      if (isAuthPage) return; // Auth sayfalarında refresh loading'i gösterme
      // Refresh sırasında global loading göstermeyelim
    };

    // localStorage ile sayfa refresh'ini tespit et
    const lastRefresh = localStorage.getItem('lastRefresh');
    const now = Date.now();
    
    if (lastRefresh) {
      const timeDiff = now - parseInt(lastRefresh);
      // 5 saniyeden az fark varsa refresh olmuş
      if (timeDiff < 5000) {
        handlePageRefresh();
      }
    }
    
    localStorage.setItem('lastRefresh', now.toString());

    // Route değişikliklerini dinle
    const handleRouteChange = () => {
      if (isAuthPage) return; // Auth sayfalarında route loading'i gösterme
      
      setIsLoading(true);
      setLoadingText("Loading Page");
      setLoadingSubtitle("Please wait...");
      
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    // Popstate event'i (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    // Beforeunload event'i (sayfa refresh/close)
    const handleBeforeUnload = () => {
      if (isAuthPage) return; // Auth sayfalarında beforeunload loading'i gösterme
      
      localStorage.setItem('isRefreshing', 'true');
      setIsLoading(true);
      setLoadingText("Saving Changes");
      setLoadingSubtitle("Please wait...");
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Sayfa yüklendiğinde refresh kontrolü
    const isRefreshing = localStorage.getItem('isRefreshing');
    if (isRefreshing === 'true' && !isAuthPage) {
      localStorage.removeItem('isRefreshing');
      handlePageRefresh();
    }

    // Visibility change event'i (tab değişikliği)
    const handleVisibilityChange = () => {
      if (!document.hidden && !isAuthPage) {
        // Tab geri geldiğinde loading göstermeyelim
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthPage]);

  const setLoading = (loading: boolean) => {
    if (isAuthPage) return; // Auth sayfalarında manuel loading'i devre dışı bırak
    setIsLoading(loading);
  };

  return (
    <LoadingContext.Provider value={{
      isLoading,
      setLoading,
      loadingText,
      setLoadingText,
      loadingSubtitle,
      setLoadingSubtitle
    }}>
      {children}
    </LoadingContext.Provider>
  );
};
